// Attract routes and audiences (docs/specs/ROUTES_AUDIENCES_EVENTS.md; migration 20261103090000). The route catalogue
// below mirrors engine/routes.py, the one source of truth: engine/tests/test_routes.py checks the two agree, and
// that the database's crm__route_catalogue() says the same. Keep the block between the markers as plain data.
//
// Attract has one set of stages for every route; what happens in each stage differs by route. Every piece is written
// for one audience (attract.audiences, or by default one per ideal-customer segment plus referrers and customers)
// and goes out on the routes that audience uses.

export type RouteKey = "website" | "blog" | "linkedin" | "meta" | "video" | "newsletter" | "ads" | "print" | "listings" | "pr";
export type StageKey = "presence" | "make" | "approve" | "publish" | "capture" | "handover";

export interface RouteDef {
  key: RouteKey;
  label: string;
  /** Who usually reads this route, in a few words. */
  who: string;
  /** content_items.kind values that go out on it. */
  content_kinds: string[];
  /** ctas.kind values it allows (1.49). */
  cta_kinds: string[];
  approval_step: string;
  publish_steps: string[];
  publish_tool: string;
  /** What Connect does first with a lead from it when the CTA does not say (connect.routes). */
  connect_route: string;
  /** The engine switch that pauses it (engineHubs.ts satellites). */
  switch: string;
}

// routes:start
export const ROUTES: RouteDef[] = [
  { key: "website", label: "Website", who: "Anyone who searches for you or follows a link", content_kinds: ["page"], cta_kinds: ["form", "link"], approval_step: "1.33", publish_steps: ["1.32", "1.33"], publish_tool: "Website studio, Cloudflare Pages or your own CMS", connect_route: "enquiry", switch: "attract:website" },
  { key: "blog", label: "Blog and articles", who: "Buyers researching a problem on Google or AI search", content_kinds: ["pillar", "blog"], cta_kinds: ["link", "form"], approval_step: "1.21", publish_steps: ["1.7", "1.35"], publish_tool: "Your site and the Ogma hub", connect_route: "enquiry", switch: "attract:content" },
  { key: "linkedin", label: "LinkedIn", who: "Decision-makers, referrers and peers", content_kinds: ["post", "video"], cta_kinds: ["link", "keyword"], approval_step: "1.21", publish_steps: ["1.6"], publish_tool: "Buffer", connect_route: "enquiry", switch: "attract:social" },
  { key: "meta", label: "Instagram and Facebook", who: "Consumers and small owners", content_kinds: ["post", "video"], cta_kinds: ["keyword", "link"], approval_step: "1.21", publish_steps: ["1.6", "1.25"], publish_tool: "Buffer, ManyChat for DMs", connect_route: "dm", switch: "attract:social" },
  { key: "video", label: "Video", who: "People who learn by watching", content_kinds: ["video", "post"], cta_kinds: ["link"], approval_step: "1.21", publish_steps: ["1.6"], publish_tool: "Per network (YouTube, TikTok)", connect_route: "enquiry", switch: "attract:social" },
  { key: "newsletter", label: "Newsletter", who: "People who opted in to hear from you", content_kinds: ["newsletter"], cta_kinds: ["link"], approval_step: "1.21", publish_steps: ["1.9", "1.56"], publish_tool: "Resend; syndication packs you paste", connect_route: "subscribe", switch: "attract:newsletter" },
  { key: "ads", label: "Digital ads", who: "Lookalikes and recent visitors", content_kinds: ["ad"], cta_kinds: ["link"], approval_step: "1.28", publish_steps: ["1.29"], publish_tool: "Meta, Google or LinkedIn ads", connect_route: "enquiry", switch: "attract:ads" },
  { key: "print", label: "Print and physical", who: "Local buyers: flyers, vans, signs, stands and mail drops", content_kinds: ["print"], cta_kinds: ["qr", "phone"], approval_step: "1.59", publish_steps: ["1.60"], publish_tool: "A print-ready pack you send to your own printer", connect_route: "enquiry", switch: "attract:print" },
  { key: "listings", label: "Listings and reviews", who: "People searching locally or in trade directories", content_kinds: ["post", "video"], cta_kinds: ["link", "phone"], approval_step: "1.21", publish_steps: ["1.43"], publish_tool: "Google Business Profile and trade listings", connect_route: "call", switch: "attract:listings" },
  { key: "pr", label: "PR and media", who: "Trade press readers and podcast listeners", content_kinds: ["pitch"], cta_kinds: ["link"], approval_step: "1.21", publish_steps: [], publish_tool: "You send or paste it", connect_route: "enquiry", switch: "attract:content" },
];
// routes:end

export const ROUTE_BY_KEY = Object.fromEntries(ROUTES.map((r) => [r.key, r])) as Record<RouteKey, RouteDef>;

type CountKey = "drafted" | "waiting" | "published" | "clicks" | "leads";

/** The six Attract stages (engine/routes.py STAGES), with the count each cell shows. */
export const STAGES: { key: StageKey; label: string; count?: CountKey; noun?: [string, string] }[] = [
  { key: "presence", label: "Set up presence" },
  { key: "make", label: "Make content", count: "drafted", noun: ["drafted", "drafted"] },
  { key: "approve", label: "You approve", count: "waiting", noun: ["waiting", "waiting"] },
  { key: "publish", label: "Publish and promote", count: "published", noun: ["published", "published"] },
  { key: "capture", label: "Capture interest", count: "clicks", noun: ["CTA click", "CTA clicks"] },
  { key: "handover", label: "Hand to Connect", count: "leads", noun: ["lead", "leads"] },
];

/** Where each route is set up in the app: its page under Attract › Accounts (the Set up presence column links here). */
export const SETUP_PAGE: Record<RouteKey, string> = {
  website: "/attract/accounts/website",
  blog: "/attract/accounts/blog",
  linkedin: "/attract/accounts/linkedin",
  meta: "/attract/accounts/meta",
  video: "/attract/accounts/video",
  newsletter: "/attract/accounts/newsletter",
  ads: "/attract/accounts/ads",
  print: "/attract/accounts/brand",
  listings: "/attract/accounts/listings",
  pr: "/attract/accounts/blog",
};

export interface Audience {
  key: string;
  label: string;
  segment: string | null;
  routes: RouteKey[];
  offers: string[];
}

/** crm_audiences */
export interface AudiencesRead {
  source: "settings" | "default";
  audiences: Audience[];
  used_routes: RouteKey[];
  required: boolean;
}

/** One row of crm_routes_board. */
export interface BoardRow {
  route: RouteKey;
  audiences: string[];
  used: boolean;
  drafted: number;
  waiting: number;
  published: number;
  clicks: number;
  leads: number;
}

export interface RoutesBoard {
  month: string;
  audience: string | null;
  routes: BoardRow[];
}

/** One piece behind a cell (crm_route_pieces). */
export interface RoutePiece {
  id: string;
  kind: string;
  title: string | null;
  status: string;
  audience: string | null;
  route: RouteKey | null;
  network: string | null;
  created_at: string;
  scheduled_at: string | null;
  published_at: string | null;
  published_url: string | null;
  clicks: number;
  leads: number;
}

export const GENERAL = "general";

/** The rows to show: routes this business uses, plus any route with work this month, in catalogue order. */
export function boardRows(board: RoutesBoard): BoardRow[] {
  return board.routes.filter((r) => r.used || r.drafted + r.waiting + r.published + r.clicks + r.leads > 0);
}

/** The number in a cell; null for Set up presence, which shows who uses the route instead. */
export function cellCount(row: BoardRow, stage: StageKey): number | null {
  const s = STAGES.find((x) => x.key === stage);
  return s?.count ? Number(row[s.count] ?? 0) : null;
}

/** "3 drafted", "1 CTA click". */
export function cellText(row: BoardRow, stage: StageKey): string | null {
  const s = STAGES.find((x) => x.key === stage);
  const n = cellCount(row, stage);
  if (n === null || !s?.noun) return null;
  return `${n} ${n === 1 ? s.noun[0] : s.noun[1]}`;
}

/** The first day of the month `offset` months from `now`, as YYYY-MM-DD (the p_month the readers take). */
export function monthParam(offset: number, now = new Date()): string {
  const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
  return d.toISOString().slice(0, 10);
}

/** An audience's label; "Everyone" for general, and the key itself for one no longer in the settings. */
export function audienceLabel(key: string | null | undefined, audiences: Audience[]): string {
  if (!key) return "No audience yet";
  if (key === GENERAL) return "Everyone";
  return audiences.find((a) => a.key === key)?.label ?? key;
}

export function routeLabel(key: string | null | undefined): string {
  if (!key) return "No route";
  return ROUTE_BY_KEY[key as RouteKey]?.label ?? key;
}

/** The routes any of the audiences use, in catalogue order. */
export function routesInUse(audiences: Audience[]): RouteKey[] {
  const used = new Set(audiences.flatMap((a) => a.routes));
  return ROUTES.map((r) => r.key).filter((k) => used.has(k));
}

const NETWORK_ROUTE: Record<string, RouteKey> = {
  linkedin: "linkedin",
  instagram: "meta",
  facebook: "meta",
  threads: "meta",
  youtube: "video",
  tiktok: "video",
  google_business: "listings",
  google_business_profile: "listings",
};
const KIND_ROUTE: Record<string, RouteKey> = {
  pillar: "blog",
  blog: "blog",
  newsletter: "newsletter",
  ad: "ads",
  pitch: "pr",
  print: "print",
  page: "website",
  video: "video",
};

/** The route a piece goes out on, from its kind and network (crm__route_for): for a row that has no route stored. */
export function routeFor(kind: string, network?: string | null): RouteKey | null {
  if ((kind === "post" || kind === "video") && network) return NETWORK_ROUTE[network.toLowerCase()] ?? (kind === "video" ? "video" : null);
  return KIND_ROUTE[kind] ?? null;
}
